import { EnumUserPermission } from "@/app/services/generated/enums/enumUserPermission.generated";
import type Key from "@/app/types/common/key";
import { RouteBuilder } from "@/app/ui/navigation/routeBuilder";
import { MenuItem } from "@/app/ui/navigation/types";
import { hasPermissionsUsingUserStore } from "@/components/pages/administration/user_roles/UserAccessHandler";
import { keys, translate } from "@/plugins/i18n";
import tenantStore from "@/store/modules/tenantStore";

interface OldTableauReport {
  translationKey: Key;
  presentView: string | null;
  historicView: string | null;
  visible?: () => boolean;
}

interface OldReportingMenuItem {
  key: Key;
  reports: OldTableauReport[];
}

const reportingMenu = {
  root: "navigation_reporting_group",
  present: "present",
  historic: "historic",

  sales: "reporting_sales",
  production: "reporting_production",
  newProduction: "reporting_new_production",
  stock: "reporting_stock",
  batchTracking: "reporting_batch_tracking",
  tasks: "reporting_tasks",
  deposits: "reporting_deposits",
};

const oldSalesMenuItem: OldReportingMenuItem = {
  key: keys.reporting_sales,
  reports: [
    {
      translationKey: keys.performance_dashboard,
      presentView: "PerformanceDashboard",
      historicView: "PerformanceDashboardHistoric",
    },
    {
      translationKey: keys.performance_graph,
      presentView: "PerformanceGraph",
      historicView: "PerformanceGraphHistoric",
    },
    {
      translationKey: keys.yoy_graph,
      presentView: "YOYGraph",
      historicView: "YOYGraphHistoric",
    },
    {
      translationKey: keys.performance_table,
      presentView: "PerformanceTable",
      historicView: "PerformanceTableHistoric",
    },
    {
      translationKey: keys.order_date_table,
      presentView: "OrderDateTable",
      historicView: "OrderDateTableHistoric",
    },
    {
      translationKey: keys.despatch_date_table,
      presentView: "DespatchDateTable",
      historicView: "DespatchDateTableHistoric",
    },
    {
      translationKey: keys.yoy_table,
      presentView: "YOYTable",
      historicView: "YOYTableHistoric",
    },
    {
      translationKey: keys.reporting_yoy_table_side_by_side,
      presentView: "YOYSide-by-Side",
      historicView: "YOYSide-by-SideHistoric",
    },
    {
      translationKey: keys.mat_table,
      presentView: "MATTable",
      historicView: "MATTable-Historic",
    },
    {
      translationKey: keys.performance_table_over_time,
      presentView: "PerformanceTableOverTime",
      historicView: "PerformanceTableOverTimeHistoric",
    },
    {
      translationKey: keys.performance_graph_by_dimension,
      presentView: "PerformanceGraphbyDimension",
      historicView: "PerformanceGraphbyDimensionHistoric",
    },
    {
      translationKey: keys.performance_by_map,
      presentView: "PerformanceMap",
      historicView: "PerformanceMapHistoric",
    },
    {
      translationKey: keys.customer_leakage_table,
      presentView: "CustomerLeakageTable",
      historicView: "CustomerLeakageTableHistoric",
    },
    {
      translationKey: keys.customer_leakage_last_delivery,
      presentView: "CustomerLeakageLastDelivery",
      historicView: "CustomerLeakageLastDeliveryHistoric",
    },
    {
      translationKey: keys.new_customer_table,
      presentView: "NewCustomerTable",
      historicView: "NewCustomerTableHistoric",
    },
    {
      translationKey: keys.litres_sold_trend_and_forecast,
      presentView: "Forecast-Trend",
      historicView: "Forecast-TrendHistoric",
    },
    {
      translationKey: keys.litres_sold_trend_and_seasonal,
      presentView: "Forecast-SeasonalandTrend",
      historicView: "Forecast-SeasonalandTrendHistoric",
    },
    {
      translationKey: keys.top_products_by_outlet,
      presentView: "TopProductsbyOutlet",
      historicView: "TopProductsbyOutletHistoric",
    },
    {
      translationKey: keys.top_outlets_by_product,
      presentView: "TopOutletsbyProduct",
      historicView: "TopOutletsbyProductHistoric",
    },
    {
      translationKey: keys.sales_by_day,
      presentView: "SalesbyDay",
      historicView: "SalesbyDayHistoric",
    },
    {
      translationKey: keys.sales_export_by_date,
      presentView: "SalesExportbyDate",
      historicView: "SalesExportbyDateHistoric",
    },
    {
      translationKey: keys.yoy_bar_chart,
      presentView: "YOYBarChart",
      historicView: "YOYBarChartHistoric",
    },
  ],
};

const oldStockMenuItem: OldReportingMenuItem = {
  key: keys.reporting_stock,
  reports: [
    {
      translationKey: keys.reporting_stock_levels_report,
      presentView: "StockValuation",
      historicView: null,
    },
    {
      translationKey: keys.reporting_wip_valuation,
      presentView: "WIPValuation",
      historicView: null,
      visible: () => !tenantStore.state.tenantSettings.isMegaBatchesEnabled,
    },
    {
      translationKey: keys.reporting_wip_valuation,
      presentView: "WIPValuationNewProd",
      historicView: null,
      visible: () => tenantStore.state.tenantSettings.isMegaBatchesEnabled,
    },
    {
      translationKey: keys.reporting_future_stock_availability,
      presentView: "FutureStockAvailability",
      historicView: "FutureStockAvailabilityHistoric",
    },
    {
      translationKey: keys.reporting_number_of_weeks_stock_cover,
      presentView: "No_WeeksStockCover",
      historicView: "No_WeeksStockCover", // This is desired - as apparently it's more discoverable.
    },
    {
      translationKey: keys.reporting_allocated_stock_by_outlet,
      presentView: "AllocatedStockbyOutlet",
      historicView: "AllocatedStockbyOutletHistoric",
    },
    {
      translationKey: keys.reporting_litres_of_alcohol,
      presentView: "LitresofAlcohol",
      historicView: null,
    },
  ],
};

const oldProductionMenuItem: OldReportingMenuItem = {
  key: keys.reporting_production,
  reports: [
    {
      translationKey: keys.volume_graph,
      presentView: "ProductionVolumeGraph",
      historicView: "ProductionVolumeGraphHistoric",
    },
    {
      translationKey: keys.packaging_graph,
      presentView: "ProductionPackagingGraph",
      historicView: "ProductionPackagingGraphHistoric",
    },
    {
      translationKey: keys.summary_table,
      presentView: "ProductionSummaryTable",
      historicView: "ProductionSummaryTableHistoric",
    },
    {
      translationKey: keys.ingredient_summary_table,
      presentView: "ProductionIngredientSummaryTable",
      historicView: "ProductionIngredientSummaryTableHistoric",
    },
    {
      translationKey: keys.recipe_cost_vs_expectation,
      presentView: "ProductionRecipeCostvsExpectation",
      historicView: "ProductionRecipeCostvsExpectationHistoric",
    },
    {
      translationKey: keys.packaging_cost_vs_expectation,
      presentView: "ProductionPackagingCostvsExpectation",
      historicView: "ProductionPackagingCostvsExpectationHistoric",
    },
    {
      translationKey: keys.reporting_vessel_movements_table,
      presentView: "VesselMovementsTable",
      historicView: "VesselMovementsTable-Historic",
    },
    {
      translationKey: keys.reporting_qc_graph,
      presentView: "QCGraph",
      historicView: "QCGraphHistoric",
    },
    {
      translationKey: keys.reporting_qc_table,
      presentView: "QCTable",
      historicView: "QCTableHistoric",
    },
  ],
};

const oldNewProductionMenuItem: OldReportingMenuItem = {
  key: keys.reporting_new_production,
  reports: [
    {
      translationKey: keys.new_production_batch_summary,
      presentView: "NewProduction-BatchSummary",
      historicView: "NewProduction-BatchSummary-Historic",
    },
    {
      translationKey: keys.new_production_process_summary,
      presentView: "NewProduction-ProcessSummaryTable",
      historicView: "NewProduction-ProcessSummaryTable-Historic",
    },
    {
      translationKey: keys.ingredient_summary_table,
      presentView: "NewProduction-IngredientSummary",
      historicView: "NewProduction-IngredientSummaryHistoric",
    },
    {
      translationKey: keys.reporting_qc_graph,
      presentView: "QCGraph-NewProduction",
      historicView: "QCGraph-NewProductionHistoric",
    },
    {
      translationKey: keys.reporting_qc_table,
      presentView: "QCTable-NewProduction",
      historicView: "QCTable-NewProductionHistoric",
    },
  ],
};

const oldBatchTrackingMenuItem: OldReportingMenuItem = {
  key: keys.reporting_batch_tracking,
  reports: [
    {
      translationKey: keys.reporting_delivery_batch_tracking,
      presentView: "DeliveryBatchTracking-Present",
      historicView: "DeliveryBatchTracking",
    },
    {
      translationKey: keys.reporting_traceable_items_used,
      presentView: "TraceableItemsUsed",
      historicView: "TraceableItemsUsedHistoric",
      visible: () => !tenantStore.state.tenantSettings.isMegaBatchesEnabled,
    },
    {
      translationKey: keys.reporting_traceable_items_used,
      presentView: "TraceableItemsUsedNewProd",
      historicView: "TraceableItemsUsedNewProdHistoric",
      visible: () => tenantStore.state.tenantSettings.isMegaBatchesEnabled,
    },
  ],
};

const oldTasksMenuItem: OldReportingMenuItem = {
  key: keys.reporting_tasks,
  reports: [
    {
      translationKey: keys.reporting_tasks_by_user,
      presentView: "TasksbyUser",
      historicView: "TasksbyUserHistoric",
    },
    {
      translationKey: keys.reporting_tasks_by_type,
      presentView: "TasksbyType",
      historicView: "TasksbyTypeHistoric",
    },
    {
      translationKey: keys.reporting_tasks_overdue,
      presentView: "OverdueTasks",
      historicView: "OverdueTasksHistoric",
    },
    {
      translationKey: keys.reporting_tasks_completed,
      presentView: "CompletedTasks",
      historicView: "CompletedTasksHistoric",
    },
  ],
};

const oldDepositsMenuItem: OldReportingMenuItem = {
  key: keys.reporting_deposits,
  reports: [
    {
      translationKey: keys.reporting_deposit_returns,
      presentView: "DepositsReturns",
      historicView: "DepositsReturnsHistoric",
    },
  ],
};

const builder = new RouteBuilder("Reporting_");
const makeNode = (
  translationKey: Key,
  workbook: string,
  view: string,
  parameters?: object,
  id?: string
) => {
  const url =
    `/reporting/reports/${workbook}/${view}` +
    (parameters ? `/${translationKey.value}` : "");
  const route = builder.route({
    url,
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "reporting" */ "@/components/pages/reporting/embeddedtableau/EmbeddedTableauReport.vue"
      ),
    componentProps: {
      workbook,
      view,
      title: translate(translationKey),
      parameters,
    },
  });

  const menuItem = MenuItem.forRoute(translationKey, route, {
    id: id ?? "reports_" + translationKey.value,
  });

  return menuItem;
};

export const buildReportingMenuItems: () => MenuItem[] = () => [
  MenuItem.forBranch({
    name: reportingMenu.root,
    icon: "Description",
    visible: () =>
      hasPermissionsUsingUserStore([EnumUserPermission.menuReportingView]),
    subMenus: [
      oldSalesMenuItem,
      oldStockMenuItem,
      oldProductionMenuItem,
      oldNewProductionMenuItem,
      oldBatchTrackingMenuItem,
      oldTasksMenuItem,
      oldDepositsMenuItem,
    ].map((menuDetails) => {
      return MenuItem.forBranch({
        name: menuDetails.key.value,
        subMenus: [
          MenuItem.forBranch({
            name: `${menuDetails.key}-present`,
            translationKey: "present",
            subMenus: menuDetails.reports
              .filter((report) => report.presentView)
              .map((report) => {
                return MenuItem.fromOpts({
                  id: `${report.translationKey.value}-presernt`,
                  name: `${menuDetails.key}-present-${report.presentView}`,
                  url: `/reporting/reports/Reporting/${report.presentView}`,
                  translationKey: report.translationKey.value,
                  view: (): Promise<any> =>
                    import(
                      /* webpackChunkName: "reporting" */ "@/components/pages/reporting/embeddedtableau/EmbeddedTableauReport.vue"
                    ),
                  componentProps: {
                    workbook: "ReportingProduction",
                    view: report.presentView,
                    title: translate(report.translationKey),
                  },
                  visible: report.visible,
                });
              }),
          }),
          MenuItem.forBranch({
            name: `${menuDetails.key}-historic`,
            translationKey: "historic",
            subMenus: menuDetails.reports
              .filter((report) => report.historicView)
              .map((report) => {
                return MenuItem.fromOpts({
                  id: `${report.translationKey.value}-historic`,
                  name: `${menuDetails.key}-historic-${report.historicView}`,
                  url: `/reporting/reports/Reporting/${report.historicView}`,
                  translationKey: report.translationKey.value,
                  view: (): Promise<any> =>
                    import(
                      /* webpackChunkName: "reporting" */ "@/components/pages/reporting/embeddedtableau/EmbeddedTableauReport.vue"
                    ),
                  componentProps: {
                    workbook: "ReportingProduction",
                    view: report.historicView,
                    title: translate(report.translationKey),
                  },
                  visible: report.visible,
                });
              }),
          }),
        ],
      });
    }),
    requiredUserPermissions: [EnumUserPermission.reporting],
  }),
  MenuItem.forBranch({
    name: keys.navigation_reporting_beta.value,
    icon: "Description",
    visible: () =>
      hasPermissionsUsingUserStore([EnumUserPermission.menuReportingView]),
    subMenus: [
      MenuItem.forBranch({
        name: keys.reporting_sales.value,
        subMenus: [
          makeNode(keys.performance_table, "SalesReports", "SalesTable"),
          makeNode(keys.performance_graph, "SalesReports", "SalesGraph"),
          makeNode(keys.yoy_graph, "SalesReports", "YoYGraph"),
          makeNode(keys.yoy_table, "SalesReports", "YoYTable"),
          makeNode(
            keys.reporting_yoy_table_side_by_side,
            "SalesReports",
            "YoYSideBySide"
          ),
          makeNode(keys.yoy_bar_chart, "SalesReports", "YoYBarChart"),
          makeNode(
            keys.performance_table_over_time,
            "SalesReports",
            "PerformanceTableOverTime"
          ),
          makeNode(keys.mat_table, "SalesReports", "MATTable"),
          makeNode(keys.performance_by_map, "SalesReports", "SalesMap"),
          makeNode(
            keys.performance_graph_by_dimension,
            "SalesReports",
            "ComparisonGraph"
          ),
          makeNode(
            keys.customer_leakage_table,
            "SalesReports",
            "CustomerLeakageTable"
          ),
          makeNode(
            keys.customer_leakage_last_delivery,
            "SalesReports",
            "CustomerLeakageLastDelivery"
          ),
          makeNode(keys.new_customer_table, "SalesReports", "NewCustomerTable"),
          makeNode(
            keys.litres_sold_trend_and_forecast,
            "SalesReports",
            "Forecast"
          ),
          makeNode(
            keys.top_products_by_outlet,
            "SalesReports",
            "SalesTableNested7",
            {
              "Parameters.Grouping": "Outlet Type",
              "Parameters.Grouping 2": "Outlet Sales Area",
              "Parameters.Grouping 3": "Outlet Sales Code",
              "Parameters.Grouping 4": "Outlet Group",
              "Parameters.Grouping 5": "Package Type",
              "Parameters.Grouping 6": "Product Brand",
              "Parameters.Grouping 7": "Product Current Name",
            }
          ),
          makeNode(
            keys.top_outlets_by_product,
            "SalesReports",
            "SalesTableNested7",
            {
              "Parameters.Grouping": "Package Type",
              "Parameters.Grouping 2": "Product Brand",
              "Parameters.Grouping 3": "Product Current Name",
              "Parameters.Grouping 4": "Outlet Type",
              "Parameters.Grouping 5": "Outlet Sales Area",
              "Parameters.Grouping 6": "Outlet Sales Code",
              "Parameters.Grouping 7": "Outlet Group",
            }
          ),
          makeNode(
            keys.sales_export_by_date,
            "SalesReports",
            "SalesExportByDate"
          ),
        ],
      }),
      MenuItem.forBranch({
        name: keys.reporting_stock.value,
        subMenus: [
          makeNode(
            keys.reporting_stock_levels_report,
            "StockReports",
            "StockValuation"
          ),
          makeNode(
            keys.reporting_future_stock_availability,
            "StockReports",
            "FutureStockAvailability"
          ),
          makeNode(
            keys.reporting_allocated_stock_by_outlet,
            "SalesReports", // This report is in the "Stock" menu, but is based on "Sales" data.
            "AllocatedStockByOutlet"
          ),
          makeNode(
            keys.reporting_number_of_weeks_stock_cover,
            "StockReports",
            "NoWeeksStockCover"
          ),
          makeNode(
            keys.reporting_litres_of_alcohol,
            "StockReports",
            "AlcoholInStock"
          ),
        ],
      }),
      MenuItem.forBranch({
        name: keys.reporting_new_production.value,
        subMenus: [
          makeNode(
            keys.new_production_batch_summary,
            "BatchReports",
            "BatchSummary"
          ),
          makeNode(
            keys.new_production_process_summary,
            "BatchReports",
            "ProcessSummary"
          ),
          makeNode(
            keys.ingredient_summary_table,
            "BatchReports",
            "IngredientSummary"
          ),
          makeNode(
            keys.reporting_wip_valuation,
            "BatchReports",
            "WIPValuation"
          ),
          makeNode(keys.reporting_qc_graph, "BatchReports", "QCGraph"),
          makeNode(keys.reporting_qc_table, "BatchReports", "QCTable"),
        ],
      }),
      MenuItem.forBranch({
        name: keys.reporting_batch_tracking.value,
        subMenus: [
          makeNode(
            keys.reporting_traceable_items_used,
            "BatchReports", // TraceableItemsUsed Report lives under "Batch Tracking" menu but is a "Batch" report.
            "TraceableItemsUsed"
          ),
          makeNode(
            keys.reporting_delivery_batch_tracking,
            "BatchTrackingReports",
            "DeliveryBatchTracking"
          ),
        ],
      }),
      MenuItem.forBranch({
        name: keys.reporting_tasks.value,
        subMenus: [
          makeNode(keys.reporting_tasks_table, "TaskReports", "TasksTable"),
          makeNode(keys.reporting_tasks_overdue, "TaskReports", "OverdueTasks"),
          makeNode(
            keys.reporting_tasks_completed,
            "TaskReports",
            "CompletedTasks"
          ),
        ],
      }),
      MenuItem.forBranch({
        name: keys.reporting_deposits.value,
        subMenus: [
          makeNode(
            keys.reporting_deposit_returns,
            "BottleDepositReports",
            "DepositsAndReturns"
          ),
        ],
      }),
    ],
    requiredUserPermissions: [EnumUserPermission.reporting],
  }),
];
